import { FieldDefinitionType } from "@/components/@types";
import { Field } from "@/components/organisms/Field";
import { FieldCell } from "@/components/organisms/Field/FieldCell";
import { getDefaultFieldCellPositionStyles } from "@/components/organisms/Field/Field.utils";
import {
  getGridCellBorderEdges,
  useResolvedGridAppearance,
} from "@/components/organisms/Table/Table.tools";
import { useTreeStore } from "@/components/organisms/Tree/Tree.store";
import { Values, ZIndex } from "@/constants";
import { CellProps, sanitizeKeyForCSS } from "@/hooks/useContextTree";
import { useSizing } from "@/hooks/useSizing";
import React from "react";

import { cn } from "@/components/ui/utils";

// Utility functions for summary nodes
const isSummaryNode = (id: string) => id?.endsWith("-summary") || false;

interface TreeCellProps {
  col: any;
  nodeId: string | null;
  data: any;
  isHeader?: boolean;
  isSticky?: boolean;
  isLoading?: boolean;
  isLastColumn?: boolean;
  isLastRow?: boolean;
  isFirstLockedColumn?: boolean;
  rowIndex?: number;
  colIndex?: number;
  cellProps?: CellProps;
  contextId?: string;
}

export function TreeCellInner({
  col,
  nodeId,
  data,
  isHeader = false,
  isSticky = false,
  isLoading = false,
  isLastColumn = false,
  isLastRow = false,
  isFirstLockedColumn = false,
  rowIndex,
  colIndex,
  cellProps,
  contextId: passedContextId,
}: TreeCellProps) {
  const storeContextId = useTreeStore((state) => state.contextId);
  const contextId = passedContextId || storeContextId;
  const layout = useTreeStore((state) => state.layout);
  const gridColors = useResolvedGridAppearance(layout?.appearance);
  const handleMouseDown = useTreeStore((state) => state.handleMouseDown);
  const columnsLockedLeft = useTreeStore((state) => state.columnsLockedLeft);
  const sanitizedKey = sanitizeKeyForCSS(col.objectKey);
  const offsetVar = `--${contextId}-column-${sanitizedKey}-offset`;
  const widthVar = `--${contextId}-column-${sanitizedKey}-width`;

  // Get node data from store using selectors
  const node = useTreeStore((state) => (nodeId ? state.treeMap[nodeId] : null));
  const isSummaryRowVisible = useTreeStore(
    (state) => state.layout?.isSummaryRowVisible ?? false,
  );
  const { cellStyles } = useSizing();
  const { minHeight, ...styles } = cellStyles;

  const positionStyles = getDefaultFieldCellPositionStyles(
    isHeader ? col.headerFieldType || col.fieldType : col.fieldType,
    "md",
  );
  const cellMeta = !isHeader && col.getCellMeta ? col.getCellMeta(data) : null;
  const isIrrelevantCell = Boolean(cellMeta?.isIrrelevant);

  let content;
  const isLocked = col.isLockedLeft || col.isLockedRight;
  // Data cells with cellProps can be navigable (headers never are)
  // Note: TreeLayout controls which rows get cellProps - sticky rows get it,
  // hidden rows underneath sticky do not
  const isNavigable = !isHeader && cellProps !== undefined;

  // Extract navigation-related props from cellProps, but NOT className
  // The outer div handles width via inline style, FieldCell should use width: 100%
  // to fill the content area (which accounts for depth padding)
  const { className: _cellClassName, ...cellNavProps } = cellProps || {};

  // Calculate depth-based padding for the first locked column
  const isFirstLockedColumnForPadding =
    columnsLockedLeft[0]?.objectKey === col.objectKey;

  const depthPadding =
    isFirstLockedColumnForPadding && node && !isHeader
      ? Math.max((node.depth - (isSummaryNode(node.id) ? 1 : 0) || 0) * 16, 0) // 16px per depth level
      : 0;

  if (isHeader) {
    if (col.renderHeader) {
      content = col.renderHeader();
    } else {
      content = (
        <Field
          type={col.headerFieldType || "text"}
          data={col.label}
          getValue={() => col.label}
          getValueOptions={() => []}
          events={null}
          isLoading={isLoading && !isFirstLockedColumn}
          style={{
            paddingLeft: "4px",
            paddingRight: "4px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            ...positionStyles,
          }}
        />
      );
    }
  } else if (node) {
    // Use FieldCell for navigable cells, Field for non-navigable (sticky, headers)
    if (isNavigable) {
      content = (
        <FieldCell
          type={col.fieldType}
          getValue={col.getValue as FieldDefinitionType["getValue"]}
          getValueOptions={col.getValueOptions}
          objectKey={col.objectKey}
          data={data}
          isEditable={col.isEditable ?? false}
          isLoading={isLoading}
          isMultiline={false}
          size="md"
          hoverBackgroundColor={null}
          style={positionStyles}
          slotProps={{
            cell: {
              // Pass navigation props (ref, id, data-*) but NOT className
              // which would set a fixed width conflicting with the parent's padding
              ...cellNavProps,
              // Only add the row class for hover effects, not the column class
              className: cn(
                `${contextId}-row-${rowIndex}`,
                "overflow-hidden",
                isIrrelevantCell && "bg-muted",
              ),
              style: {
                ...styles,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0, // Override FieldCell's minWidth: fit-content
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                ...(isIrrelevantCell && {
                  color:
                    "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
                }),
              },
              radius: "0px",
            },
            // Pass padding to content layer so focus outline covers full cell
            content: {
              style: {
                paddingLeft: `${depthPadding}px`,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0, // Override any fit-content
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            },
          }}
        />
      );
    } else {
      // Use Field for non-navigable cells (sticky rows, etc.)
      content = (
        <Field
          type={col.fieldType}
          data={data}
          getValue={col.getValue as FieldDefinitionType["getValue"]}
          getValueOptions={col.getValueOptions}
          events={null}
          isLoading={isLoading}
          style={positionStyles}
        />
      );
    }
  }

  const isLastLockedColumnLeft = useTreeStore((state) =>
    state.columnsLockedLeft.length > 0
      ? state.columnsLockedLeft[state.columnsLockedLeft.length - 1]
          ?.objectKey === col.objectKey
      : false,
  );
  const isFirstLockedColumnRight = useTreeStore(
    (state) => state.columnsLockedRight[0]?.objectKey === col.objectKey,
  );

  const hideContent =
    node?.isExpanded &&
    isSummaryRowVisible &&
    !isSticky &&
    !col.isLockedLeft &&
    !col.isLockedRight;

  const borderEdges = getGridCellBorderEdges({
    color: "var(--border)",
    showRightEdge: !(layout?.isCellBordersHidden && !isLocked),
    isOuterRight: isLastColumn,
    isOuterBottom: Boolean(isLastRow && !isHeader),
    hideOuterEdges: layout?.hideOuterBorders,
  });

  // Build class names for the outer div
  const outerClassName = [
    `${contextId}-column-${sanitizedKey}`, // Column class for width from useResizeColumn
    !isHeader ? `${contextId}-row-${rowIndex}` : "", // Row class for hover (including sticky rows)
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      key={col.objectKey + isLoading.toString()}
      className={outerClassName}
      data-grid-cell={!isHeader ? true : undefined}
      onFocus={
        isNavigable && nodeId
          ? () => layout?.onCellFocus?.(nodeId, col.objectKey)
          : undefined
      }
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        transform: `translateX(var(${offsetVar}))`,
        // Width is controlled by the column class from useResizeColumn
        // but we also set it inline as a fallback
        width: `var(${widthVar})`,
        height: isHeader
          ? Values.DEFAULT_ROW_HEIGHT
          : Values.DEFAULT_ROW_HEIGHT,
        boxSizing: "border-box",
        ...borderEdges,
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        backgroundColor: isHeader
          ? gridColors.headerBackground
          : isIrrelevantCell
            ? "var(--muted)"
            : "var(--grid-row-bg)",
        cursor: "default",
        display: "flex",
        alignItems: "center",
        // overflow: "hidden",
        zIndex:
          col.zIndex ?? (isHeader ? ZIndex.GRID.HEADER_CELL : ZIndex.GRID.ROW),
        paddingLeft: isHeader ? "4px" : 0,
        paddingRight: isHeader ? "4px" : 0,
        ...positionStyles,
        ...(isSummaryNode(node?.id) && { fontWeight: 600 }),
        ...(isIrrelevantCell && {
          color:
            "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
        }),
      }}
      data-tree-cell-irrel={isIrrelevantCell ? "true" : "false"}
    >
      {isLastLockedColumnLeft && (
        <div
          key={`${col.objectKey}-left-lock`}
          style={{
            position: "absolute",
            top: 0,
            right: -5,
            bottom: 0,
            width: "4px",
            background: `linear-gradient(90deg,
              rgba(0, 0, 0, 0.04),
              rgba(0, 0, 0, 0)
          )`,
          }}
        />
      )}
      {isFirstLockedColumnRight && (
        <div
          key={`${col.objectKey}-right-lock`}
          style={{
            position: "absolute",
            top: 0,
            left: -5,
            bottom: 0,
            width: "4px",
            background: `linear-gradient(90deg,
              rgba(0, 0, 0, 0.02),
              rgba(0, 0, 0, 0)
          )`,
          }}
        />
      )}
      {isSummaryNode(node?.id) && col.isLockedLeft && (
        <div
          style={{
            height: "1px",
            position: "absolute",
            top: -1,
            right: 0,
            width: `calc(100% - ${depthPadding}px)`,
            backgroundColor: "var(--border)",
            zIndex: ZIndex.GRID.HEADER_FIXED_ELEMENT,
          }}
        />
      )}
      {!hideContent && content}
      {isHeader && col.isResizable && (
        <div
          onMouseDown={handleMouseDown(col.objectKey)}
          style={{
            position: "absolute",
            zIndex: ZIndex.GRID.COLUMN_RESIZE,
            right: -0.5,
            top: "50%",
            transform: "translate(45%, -50%)",
            width: 4,
            height: 32,
            borderRadius: "2px",
            backgroundColor: "transparent",
            transition: "all 0.3s",
            cursor: "col-resize",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--primary)";
            e.currentTarget.style.zIndex = String(
              ZIndex.GRID.COLUMN_RESIZE_HOVER,
            );
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.zIndex = String(ZIndex.GRID.COLUMN_RESIZE);
          }}
        />
      )}
    </div>
  );
}

export const TreeCell = React.memo(TreeCellInner, (prevProps, nextProps) => {
  return (
    prevProps.col.objectKey === nextProps.col.objectKey &&
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.data === nextProps.data &&
    prevProps.isHeader === nextProps.isHeader &&
    prevProps.isSticky === nextProps.isSticky &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.isLastRow === nextProps.isLastRow &&
    prevProps.isLastColumn === nextProps.isLastColumn &&
    prevProps.isFirstLockedColumn === nextProps.isFirstLockedColumn &&
    prevProps.rowIndex === nextProps.rowIndex &&
    prevProps.colIndex === nextProps.colIndex &&
    prevProps.contextId === nextProps.contextId &&
    // Check if cellProps presence changed (for navigation registration)
    (prevProps.cellProps !== undefined) ===
      (nextProps.cellProps !== undefined) &&
    // Check if data-row-index changed (for correct registration key)
    prevProps.cellProps?.["data-row-index"] ===
      nextProps.cellProps?.["data-row-index"]
  );
});
